import React, { useState, useEffect, useRef, useMemo } from "react";
import * as XLSX from "xlsx";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../Header/header";
import { Table, Form } from "react-bootstrap";
import { MdFileUpload } from "react-icons/md";
import { Tooltip } from '@mui/material';
import { fetchGadgetCaseDetailsByVendor } from "../../api/api";
import { Circles } from "react-loader-spinner";
import DownloadIcon from '@mui/icons-material/Download';
import Swal from "sweetalert2";
import { statusColor } from "../../utils/statusColor";
function Vendor_name() {
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedAAno, setSelectedAAno] = useState([]);
  const [aaNumber, setAaNumber] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [filterType, setFilterType] = useState("closure");
  const [isExcelUpload, setIsExcelUpload] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const itemsPerPage = 10;
  const location = useLocation();
  const { vendorName, vendorId, isgstRegistered } = location.state || {};

  const { filteredInvoices, totalFilteredCount } = useMemo(() => {
    let result = [...invoices];

    if (fromDate && toDate) {
      const parseInputDate = (dateStr) => {
        const [year, month, day] = dateStr.split("-").map(Number);
        return new Date(year, month - 1, day);
      };

      const parseInvoiceDate = (dateStr) => {
        if (!dateStr || typeof dateStr !== "string") return null;
        const [datePart] = dateStr.split(" ");
        const [day, month, year] = datePart.split("-").map(Number);
        return new Date(year, month - 1, day);
      };

      const from = parseInputDate(fromDate);
      const to = parseInputDate(toDate);
      to.setDate(to.getDate() + 1);

      result = result.filter((invoice) => {
        const dateField = filterType === "creation"
          ? invoice.creationDate
          : invoice.closureDate;

        if (!dateField) return false;
        const invoiceDate = parseInvoiceDate(dateField);
        return invoiceDate && invoiceDate >= from && invoiceDate < to;
      });
    }

    // AA Number filtering
    if (aaNumber) {
      result = result.filter((inv) =>
        inv.aA_Number?.toLowerCase().includes(aaNumber.toLowerCase())
      );
    }

    return {
      filteredInvoices: result,
      totalFilteredCount: result.length
    };
  }, [invoices, fromDate, toDate, filterType, aaNumber]);

  useEffect(() => {
    const getTodayDate = () => {
      const today = new Date();
      return today.toISOString().split('T')[0];
    };

    const get30DaysAgoDate = () => {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return thirtyDaysAgo.toISOString().split('T')[0];
    };

    if (!fromDate && !toDate) {
      setFromDate(get30DaysAgoDate());
      setToDate(getTodayDate());
    }
  }, [fromDate, toDate]);
  const totalPages = Math.ceil(totalFilteredCount / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = Math.max(indexOfLastItem - itemsPerPage, 0);
  const currentInvoices = filteredInvoices.slice(indexOfFirstItem, indexOfLastItem);

  useEffect(() => {
    setCurrentPage(1);
  }, [fromDate, toDate, aaNumber, filterType]);

  useEffect(() => {
    const fetchData = async () => {
      if (vendorName) {
        try {
          const result = await fetchGadgetCaseDetailsByVendor(vendorName, vendorId);
          if (result.success) {
            setInvoices((result.data || []).map(inv => ({ ...inv, isChecked: false })));
          }
        } finally {
          setLoading(false);
        }
      }
    };
    fetchData();
  }, [vendorName]);

  const formatDate = (dateTimeStr) => {
    if (!dateTimeStr || typeof dateTimeStr !== "string") return "--";
    if (!isNaN(dateTimeStr)) {
      const serial = parseFloat(dateTimeStr);
      const utc_days = Math.floor(serial - 25569);
      const utc_value = utc_days * 86400;
      return new Date(utc_value * 1000).toLocaleDateString("en-GB");
    }
    if (dateTimeStr.includes("-")) {
      const parts = dateTimeStr.split("-");
      if (parts.length === 3) {
        return parts[0].length === 4
          ? `${parts[2]}/${parts[1]}/${parts[0]}`
          : `${parts[0]}/${parts[1]}/${parts[2]}`;
      }
    }
    return "--";
  };

  const handleCheckboxChange = (invoiceId, type = "row") => {
    if (type === "all") {
      const filteredIds = filteredInvoices.map(inv => inv.aA_Number);
      const allFilteredSelected = filteredIds.every(id => selectedAAno.includes(id));

      if (allFilteredSelected) {
        const newSelected = selectedAAno.filter(id => !filteredIds.includes(id));
        setSelectedAAno(newSelected);
        setInvoices(prev =>
          prev.map(inv => ({
            ...inv,
            isChecked: newSelected.includes(inv.aA_Number)
          }))
        );
      } else {
        const newSelected = [...new Set([...selectedAAno, ...filteredIds])];
        setSelectedAAno(newSelected);
        setInvoices(prev =>
          prev.map(inv => ({
            ...inv,
            isChecked: newSelected.includes(inv.aA_Number)
          }))
        );
      }
    }

    else if (type === "page") {
      const pageIds = currentInvoices.map(inv => inv.aA_Number);
      const allPageSelected = pageIds.every(id => selectedAAno.includes(id));

      if (allPageSelected) {
        const newSelected = selectedAAno.filter(id => !pageIds.includes(id));
        setSelectedAAno(newSelected);
        setInvoices(prev =>
          prev.map(inv => ({
            ...inv,
            isChecked: newSelected.includes(inv.aA_Number)
          }))
        );
      } else {
        const newSelected = [...new Set([...selectedAAno, ...pageIds])];
        setSelectedAAno(newSelected);
        setInvoices(prev =>
          prev.map(inv => ({
            ...inv,
            isChecked: newSelected.includes(inv.aA_Number)
          }))
        );
      }
    }

    else {
      setSelectedAAno(prev => {
        const newSelected = prev.includes(invoiceId)
          ? prev.filter(id => id !== invoiceId)
          : [...prev, invoiceId];

        setInvoices(prevInvoices =>
          prevInvoices.map(inv =>
            inv.aA_Number === invoiceId
              ? { ...inv, isChecked: !inv.isChecked }
              : inv
          )
        );
        return newSelected;
      });
    }
  };

  const handleSubmit = () => {
    const selectedInvoiceData = invoices.filter(invoice =>
      selectedAAno.includes(invoice.aA_Number)
    );
    console.log(selectedInvoiceData, 'invoicee selectt ')
    if (selectedInvoiceData.length === 0) {
      alert("Please select at least one invoice before submitting.");
      return;
    }

    navigate("/vendorBatchPage", {
      state: {
        selectedInvoices: selectedInvoiceData,
        vendorName,
        vendorId,
        isExcelUpload,
        isgstRegistered
      },
    });

    setInvoices(prev => prev.map(inv => ({ ...inv, isChecked: false })));
    setSelectedAAno([]);
    setCurrentPage(1);
    setIsExcelUpload(false);
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement("a");
    link.href = "/templates/invoice_template.xlsx";
    link.download = "invoice_template.xlsx";
    link.click();
  };

  const fileInputRef = useRef(null);

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };
  const normalizeValue = (value) => {
    if (value === null || value === undefined) return "";
    return value
      .toString()
      .trim()
      .replace(/\s+/g, "")
      .replace(/\u00A0/g, "")
      .replace(/\r/g, "")
      .replace(/\n/g, "")
      .replace(/[^\w]/g, "")
      .toLowerCase();
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);

    const reader = new FileReader();
    const fileExtension = file.name.split(".").pop().toLowerCase();

    reader.onload = (e) => {
      let data;
      try {
        if (fileExtension === "csv") {
          const csvData = e.target.result;
          const workbook = XLSX.read(csvData, { type: "string" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        } else {
          const binaryData = new Uint8Array(e.target.result);
          const workbook = XLSX.read(binaryData, { type: "array" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        }

        const [headers, ...rows] = data;
        const formattedData = rows.map((row) =>
          headers.reduce((acc, key, i) => {
            const mappedKey =
              {
                "AA Number": "aA_Number",
                "IMEI Number": "imeiNumber",
                "Customer Name": "customerName",
                "Service Type": "serviceType",
                "Brand": "brand",
                "Make Model": "makeModel",
                "Repair Charges": "repairCharges",
                "Service Charges(Pre GST)": "serviceCharges",
                "Total": "total",
              }[key] || key;

            let value = row[i];
            acc[mappedKey] = value;
            return acc;
          }, {})
        ).filter(item => item.aA_Number || item.imeiNumber);

        // Normalize existing values
        const existingAANumbers = new Set(
          invoices.map((inv) => normalizeValue(inv.aA_Number))
        );
        const existingIMEIs = new Set(
          invoices.map((inv) => normalizeValue(inv.imeiNumber))
        );

        const notFoundRecords = formattedData.filter((item) => {
          const aa = normalizeValue(item.aA_Number);
          const imei = normalizeValue(item.imeiNumber);
          const aaExists = existingAANumbers.has(aa);
          const imeiExists = existingIMEIs.has(imei);
          return !aaExists || !imeiExists;
        });

        if (notFoundRecords.length > 0) {
          setUploading(false);

          const tableRows = notFoundRecords
            .slice(0, 10)
            .map(record => `
          <tr>
            <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${record.aA_Number || 'N/A'}</td>
            <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${record.imeiNumber || 'N/A'}</td>
          </tr>
        `).join('');

          let moreRecordsMessage = '';
          if (notFoundRecords.length > 10) {
            moreRecordsMessage = `<tr><td colspan="2" style="border: 1px solid #ddd; padding: 8px; text-align: center; background-color: #f8f9fa;">... and ${notFoundRecords.length - 10} more records</td></tr>`;
          }

          const errorHTML = `
        <div style="text-align: left; max-height: 400px; overflow-y: auto;">
          <p style="margin-bottom: 15px; font-weight: 500;">These AA Numbers and IMEI Numbers are not available in the system, Please verify the data and upload again.</p>
          <table style="width: 100%; border-collapse: collapse; border: 1px solid #ddd;">
            <thead>
              <tr style="background-color: #f8f9fa;">
                <th style="border: 1px solid #ddd; padding: 10px; text-align: left; width: 50%;">AA Number</th>
                <th style="border: 1px solid #ddd; padding: 10px; text-align: left; width: 50%;">IMEI Number</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
              ${moreRecordsMessage}
            </tbody>
          </table>
        </div>
      `;

          Swal.fire({
            title: "Records Not Found",
            html: errorHTML,
            icon: "error",
            confirmButtonText: "OK",
            width: 700,
            customClass: {
              popup: 'custom-swal-popup'
            }
          });
          return;
        }

        setSelectedInvoices(formattedData);
        navigate("/vendorBatchPage", {
          state: {
            selectedInvoices: formattedData,
            vendorName,
            vendorId,
            isExcelUpload: true,
          },
        });

      } catch (error) {
        setUploading(false);
        Swal.fire({
          title: "Error",
          text: "Error processing file. Please try again.",
          icon: "error",
          confirmButtonText: "OK"
        });
      }
    };

    reader.onerror = () => {
      setUploading(false);
      Swal.fire({
        title: "Error",
        text: "Error reading file. Please try again.",
        icon: "error",
        confirmButtonText: "OK"
      });
    };

    if (fileExtension === "csv") {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };
  const handleNext = () => currentPage < totalPages && setCurrentPage(currentPage + 1);
  const handlePrevious = () => currentPage > 1 && setCurrentPage(currentPage - 1);

  return (
    <div>
      <div className="container mt-4">
        <div className="netwrok_table_main_content">
          <div className="p-3 border rounded" style={{ backgroundColor: "#f9f9f9" }}>
            <div className="mb-3">
              <h5 className="fw-bold m-0">
                {vendorName} / {vendorId}
              </h5>
            </div>

            {/* Filter UI remains the same... */}
            <div className="d-flex flex-wrap justify-content-between align-items-end gap-3 mb-3">
              <div className="d-flex flex-wrap align-items-end gap-3">
                <div>
                  <label className="form-label">Filter By</label>
                  <select
                    className="form-select"
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    {/* <option value="creation">Creation Date</option> */}
                    <option value="closure">Closure Date</option>
                  </select>
                </div>

                <div>
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    max={toDate || new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div>
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    min={fromDate}
                  />
                </div>

                <div className="d-flex gap-2">
                  <button className="btn btn-secondary" onClick={() => setCurrentPage(1)}>
                    Apply Filter
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setFromDate("");
                      setToDate("");
                    }}
                  >
                    Reset
                  </button>
                </div>

                <div>
                  <label className="form-label">Search by AA Number</label>
                  <input
                    type="text"
                    className="form-control"
                    value={aaNumber}
                    placeholder="Enter AA Number"
                    onChange={(e) => setAaNumber(e.target.value)}
                  />
                </div>
              </div>

              <div className="d-flex gap-3">
                <Tooltip title="Download sample Excel" arrow>
                  <button
                    className="btn btn-outline-primary"
                    onClick={handleDownloadTemplate}
                    style={{
                      border: "2px solid #8000d7",
                      color: "#8000d7",
                      borderRadius: "8px",
                    }}
                  >
                    <DownloadIcon />
                  </button>
                </Tooltip>

                <Tooltip title="Upload Excel" arrow>
                  <button
                    className="btn btn-primary d-flex align-items-center"
                    onClick={triggerFileInput}
                    style={{
                      backgroundColor: "#8000d7",
                      border: "none",
                      padding: "10px 16px",
                      borderRadius: "8px",
                    }}
                  >
                    <MdFileUpload />
                  </button>
                </Tooltip>
              </div>
            </div>
            {/* 
            <div className="text-muted mb-2">
              Showing {filteredInvoices.length} records
              {fromDate && toDate &&
                ` (filtered by ${filterType} date between ${formatDate(fromDate)} and ${formatDate(toDate)})`
              }
              {aaNumber && ` (and AA number containing "${aaNumber}")`}
            </div> */}

            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept=".xlsx, .xls, .csv"
              onChange={handleFileUpload}
            />
          </div>
          {uploading && (
            <div className="overlay-loader">
              <div className="loader-content text-center">
                <Circles
                  height="80"
                  width="80"
                  color="#8000d7"
                  ariaLabel="circles-loading"
                  visible={true}
                />
                <p className="mt-3" style={{ color: "#8000d7", fontWeight: "500" }}>
                  Processing your data and redirecting...
                </p>
              </div>
            </div>
          )}
          {loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
              <Circles height="60" width="60" color="#FE850E" ariaLabel="circles-loading" visible={true} />
            </div>
          ) : (
            <>
              <div className="table-responsive mt-3">
                <Table className="bg-white text-center border-0 network_table">
                  <thead style={{ backgroundColor: "#EEF4FF" }}>
                    <tr className="text-dark fw-semibold table_th_border">
                      <th className="border-start d-flex gap-4">
                        <span className="d-flex gap-2"><Form.Check
                          type="checkbox"
                          checked={
                            filteredInvoices.length > 0 &&
                            filteredInvoices.every(inv => selectedAAno.includes(inv.aA_Number))
                          }
                          onChange={() => handleCheckboxChange("all", "all")}
                          disabled={filteredInvoices.length === 0}
                        /> All</span>
                        <span className="d-flex gap-2"><Form.Check
                          checked={currentInvoices.length > 0 &&
                            currentInvoices.every(inv => selectedAAno.includes(inv.aA_Number))}
                          indeterminate={
                            currentInvoices.some(inv => selectedAAno.includes(inv.aA_Number)) &&
                            !currentInvoices.every(inv => selectedAAno.includes(inv.aA_Number))
                          }
                          onChange={() => handleCheckboxChange("page", "page")}
                        />Per-Page</span>
                      </th>
                      <th style={{ whiteSpace: "nowrap" }}>AA No</th>
                      <th style={{ whiteSpace: "nowrap" }}>IMEI No</th>
                      <th style={{ whiteSpace: "nowrap" }}>Creation Date</th>
                      <th style={{ whiteSpace: "nowrap" }}>Closure Date</th>
                      <th style={{ whiteSpace: "nowrap" }}>Selling Partner</th>
                      <th style={{ whiteSpace: "nowrap" }}>Vendor Name</th>
                      <th style={{ whiteSpace: "nowrap" }}>Customer Name</th>
                      <th style={{ whiteSpace: "nowrap" }}>Service Type</th>
                      <th style={{ whiteSpace: "nowrap" }}>Brand</th>
                      <th style={{ whiteSpace: "nowrap" }}>Make Model</th>
                      <th style={{ whiteSpace: "nowrap" }}>Repair Charges</th>
                      <th style={{ whiteSpace: "nowrap" }}>Logistics and Repair Support Fees(Pre GST)</th>
                      <th>Total</th>
                      <th style={{ whiteSpace: "nowrap" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentInvoices.length > 0 ? (

                      currentInvoices.map((invoice) => (
                        <tr key={invoice.aA_Number} className="text-center border-bottom network_td_item">
                          <td className="align-middle vendor_checkbox">
                            <input
                              key={invoice.aA_Number}
                              type="checkbox"
                              checked={selectedAAno.includes(invoice.aA_Number)}
                              onChange={() => handleCheckboxChange(invoice.aA_Number, "row")}
                            />
                          </td>
                          <td className="align-middle">{invoice.aA_Number || "--"}</td>
                          <td className="align-middle">{invoice.imeiNumber || "--"}</td>
                          <td className="align-middle">
                            {invoice.creationDate ? formatDate(invoice.creationDate).split(" ")[0] : "--"}
                          </td>
                          <td className="align-middle">
                            {invoice.closureDate ? formatDate(invoice.closureDate).split(" ")[0] : "--"}
                          </td>
                          <td className="align-middle">{invoice.dealer || "--"}</td>
                          <td className="align-middle">{invoice.sellingPartner || "--"}</td>

                          <td className="align-middle">{invoice.customerName || "--"}</td>
                          <td className="align-middle">{invoice.serviceType || "--"}</td>
                          <td className="align-middle">{invoice.brand || "--"}</td>
                          <td className="align-middle">{invoice.makeModel || "--"}</td>
                          <td className="align-middle">{invoice.repairCharges || "--"}</td>
                          <td className="align-middle">{invoice.serviceCharges || "--"}</td>
                          <td className="align-middle">{invoice.total || "--"}</td>
                          <td
                            className="align-middle"
                            style={{ minWidth: "180px", whiteSpace: "nowrap" }}
                          >
                            <span
                              className="badge"
                              style={{
                                backgroundColor:
                                  statusColor[invoice.invoiceStatus?.trim()]?.backgroundColor || "#F3F4F6",
                                color:
                                  statusColor[invoice.invoiceStatus?.trim()]?.color || "#6B7280",
                                border: `1px solid ${statusColor[invoice.invoiceStatus?.trim()]?.color || "#6B7280"
                                  }`,
                                fontSize: "13px",
                                padding: "8px 14px",
                                fontWeight: "600",
                              }}
                            >
                              {invoice.invoiceStatus?.trim() || "Ready For Billing"}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="14" className="text-center py-4">
                          Data not available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              <div className="d-flex justify-content-between align-items-center pagination-container network_previous">
                <button
                  className="network_previous"
                  onClick={handlePrevious}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>

                <span className="page-info">
                  Page {currentPage} of {totalPages} -
                  Showing {Math.min(indexOfLastItem, totalFilteredCount)} of {totalFilteredCount} records
                </span>

                <button
                  className="network_previous"
                  onClick={handleNext}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </>
          )}

          {invoices.some((inv) => inv.isChecked) ? (
            // Only show submit button when items are selected
            <div className="d-flex justify-content-end mt-4">
              <button
                className="btn btn-primary d-flex align-items-center"
                style={{
                  backgroundColor: "#8000d7",
                  border: "none",
                  padding: "10px 50px",
                  borderRadius: "8px",
                  fontWeight: "500",
                  fontSize: "16px",
                }}
                onClick={handleSubmit}
              >
                Submit
              </button>
            </div>
          ) : (
            // Show the full selection UI when no items are selected
            <div className="d-flex mt-4 vendor_submit_btn justify-content-between align-items-center flex-wrap gap-3">
              <div className="d-flex flex-wrap gap-2">
                {selectedAAno.map((aaNo, index) => {
                  const aaNumber =
                    invoices.find((inv) => inv.aA_Number === aaNo)?.aA_Number ||
                    aaNo;

                  return (
                    <p
                      key={index}
                      className="vendor_srn_no d-flex align-items-center"
                    >
                      AA no <span className="ms-1">{aaNumber}</span>
                      <button
                        type="button"
                        className="btn-close ms-2"
                        onClick={() => {
                          setSelectedAAno((prev) =>
                            prev.filter((item) => item !== aaNo)
                          );
                          setInvoices((prevInvoices) =>
                            prevInvoices.map((invoice) =>
                              invoice.aA_Number === aaNo
                                ? { ...invoice, isChecked: false }
                                : invoice
                            )
                          );
                        }}
                        aria-label="Remove"
                      ></button>
                    </p>
                  );
                })}
              </div>

              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn btn-primary d-flex align-items-center"
                  style={{
                    backgroundColor: "#8000d7",
                    border: "none",
                    padding: "10px 50px",
                    borderRadius: "8px",
                    fontWeight: "500",
                    fontSize: "16px",
                    opacity: invoices.some((inv) => inv.isChecked) ? 1 : 0.6,
                    cursor: invoices.some((inv) => inv.isChecked)
                      ? "pointer"
                      : "not-allowed",
                  }}
                  onClick={handleSubmit}
                  disabled={!invoices.some((inv) => inv.isChecked)}
                >
                  Submit
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Vendor_name;